import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { CircleAlert, ClipboardList, PackageCheck, PackageSearch, Pencil, Undo2, type LucideIcon } from 'lucide-react';
import type { AbastecimentoHistoricoEventoView, SolicitacaoAbastecimentoDetalheView, TipoEventoHistoricoAbastecimento } from '@pieltec/contracts';
import { FormDialog, FormDialogBody, FormDialogContent, FormDialogDescription, FormDialogHeader, FormDialogTitle } from '../../components/ui/form-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { abastecimentoApi } from '../../lib/estoque/abastecimento-api';
import { formatarDataHoraOperacional, formatarQuantidadeDecimal } from '../../lib/data-hora-operacional';
import { linhaDaFila } from './abastecimento-linha';

const EVENTO: Record<TipoEventoHistoricoAbastecimento, { readonly rotulo: string; readonly Icone: LucideIcon }> = {
  CRIACAO: { rotulo: 'Criação da solicitação', Icone: ClipboardList },
  AJUSTE: { rotulo: 'Ajuste de quantidade', Icone: Pencil },
  SEPARACAO: { rotulo: 'Separação (não movimenta estoque)', Icone: PackageSearch },
  ENTREGA: { rotulo: 'Entrega física', Icone: PackageCheck },
  ESTORNO_ENTREGA: { rotulo: 'Estorno de entrega', Icone: Undo2 },
};
const BOTAO = 'inline-flex min-h-11 items-center justify-center rounded-md border border-border px-4 font-semibold';

export type EstadoDetalhe = 'carregando' | 'erro' | 'pronto';

export interface DetalheSolicitacaoCorpoProps {
  readonly estado: EstadoDetalhe;
  readonly detalhe?: SolicitacaoAbastecimentoDetalheView | null;
  readonly onTentarNovamente?: () => void;
}

/** Ordem cronológica estável: o servidor já ordena por instante e id; empates preservam a ordem recebida. */
export function historicoCronologico(historico: readonly AbastecimentoHistoricoEventoView[]): readonly AbastecimentoHistoricoEventoView[] {
  return historico
    .map((evento, indice) => ({ evento, indice, instante: Date.parse(evento.ocorridoEm) }))
    .sort((a, b) => (Number.isNaN(a.instante) || Number.isNaN(b.instante) || a.instante === b.instante ? a.indice - b.indice : a.instante - b.instante))
    .map(({ evento }) => evento);
}

function EventoHistorico({ evento }: { readonly evento: AbastecimentoHistoricoEventoView }): ReactElement {
  const { rotulo, Icone } = EVENTO[evento.tipo];
  return (
    <li className="min-w-0 space-y-1 border-b border-border p-4 text-sm last:border-b-0">
      <p className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 font-semibold"><Icone className="size-4" aria-hidden="true" />{rotulo}</span>
        <time className="tabular-nums text-muted-foreground">{formatarDataHoraOperacional(evento.ocorridoEm)}</time>
      </p>
      <p className="text-xs text-muted-foreground">{evento.atorNome || 'Responsável não informado'}</p>
      <p className="tabular-nums">{formatarQuantidadeDecimal(evento.quantidade, evento.unidade)}</p>
      {evento.motivo && <p className="break-words">Motivo: {evento.motivo}</p>}
      {(evento.localizacaoOrigemId || evento.localizacaoDestinoId) && (
        <p className="break-words text-xs text-muted-foreground">
          {evento.localizacaoOrigemId && <span>Origem: {evento.localizacaoOrigemId}</span>}
          {evento.localizacaoOrigemId && evento.localizacaoDestinoId && ' · '}
          {evento.localizacaoDestinoId && <span>Destino: {evento.localizacaoDestinoId}</span>}
        </p>
      )}
    </li>
  );
}

/** Conteúdo do detalhe (UI-51, UI-52, UI-54, D-10): fatos da solicitação, fontes previstas e histórico com rolagem interna. */
export function DetalheSolicitacaoCorpo({ estado, detalhe, onTentarNovamente }: DetalheSolicitacaoCorpoProps): ReactElement {
  if (estado === 'carregando') {
    return (
      <div aria-busy="true" className="space-y-4">
        <div data-slot="skeleton-cabecalho" className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
        <div data-slot="skeleton-corpo" className="space-y-2"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>
      </div>
    );
  }
  if (estado === 'erro' || !detalhe) {
    return (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-danger p-4 text-sm text-danger">
        <span className="inline-flex items-center gap-2"><CircleAlert className="size-4" aria-hidden="true" />Não foi possível carregar os dados. Verifique a conexão e tente novamente.</span>
        <button type="button" className={BOTAO} onClick={onTentarNovamente}>Tentar novamente</button>
      </div>
    );
  }
  const { solicitacao, fontes } = detalhe;
  const linha = linhaDaFila(solicitacao);
  const eventos = historicoCronologico(detalhe.historico);
  return (
    <div className="space-y-6">
      <section aria-label="Fatos da solicitação" className="min-w-0 space-y-2">
        <p className="break-words font-semibold">{linha.material}</p>
        <p className="break-words text-sm">{linha.destino}</p>
        <p className="text-xs text-muted-foreground"><span className="font-semibold" data-slot="origem-detalhe">{linha.origemTexto}</span> · {linha.natureza}</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
          <div><dt className="text-xs font-semibold">Solicitado</dt><dd className="text-right tabular-nums">{linha.solicitadoTexto}</dd></div>
          <div><dt className="text-xs font-semibold">Separado</dt><dd className="text-right tabular-nums">{linha.separadoTexto}</dd></div>
          <div><dt className="text-xs font-semibold">Entregue</dt><dd className="text-right tabular-nums">{linha.entregueTexto}</dd></div>
          <div><dt className="text-xs font-semibold">Pendente</dt><dd className="text-right tabular-nums font-semibold">{linha.pendenteTexto}</dd></div>
        </dl>
        <p className="break-words text-xs text-muted-foreground">{linha.solicitante} · {linha.criadaEm}</p>
      </section>
      {fontes.length > 0 && (
        <section aria-label="Fontes previstas" className="space-y-1">
          <h3 className="text-xs font-semibold">Fontes previstas</h3>
          <ul className="text-sm">
            {fontes.map((fonte, indice) => (
              <li key={`${fonte.etapaOrdem ?? 'sem-etapa'}-${indice}`} className="flex justify-between gap-4">
                <span>{fonte.etapaOrdem === null ? 'Etapa não informada' : `Etapa ${fonte.etapaOrdem}`}</span>
                <span className="tabular-nums">{formatarQuantidadeDecimal(fonte.quantidade, solicitacao.unidade)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section aria-label="Histórico da solicitação" className="space-y-2">
        <h3 className="text-xs font-semibold">Histórico</h3>
        {eventos.length === 0
          ? <p className="text-sm text-muted-foreground">Nenhum evento registrado para esta solicitação.</p>
          : (
            <ol data-slot="historico-rolagem" tabIndex={0} aria-label="Eventos em ordem cronológica" className="max-h-64 overflow-y-auto overflow-x-hidden rounded-md border border-border">
              {eventos.map((evento, indice) => <EventoHistorico key={`${evento.tipo}:${evento.id}:${indice}`} evento={evento} />)}
            </ol>
          )}
      </section>
    </div>
  );
}

export interface SolicitacaoAbastecimentoDetalheProps {
  readonly solicitacaoId: string;
  /** Item (linha da fila) aberto: restringe cabeçalho, fontes e entregas ao material do cartão clicado. */
  readonly itemId?: string;
  readonly aberto: boolean;
  readonly onAbertoChange: (aberto: boolean) => void;
}

/** Diálogo centralizado que carrega o detalhe com histórico (D-10). Leitura apenas: nenhuma ação de escrita aqui. */
export function SolicitacaoAbastecimentoDetalhe({ solicitacaoId, itemId, aberto, onAbertoChange }: SolicitacaoAbastecimentoDetalheProps): ReactElement {
  const [estado, setEstado] = useState<EstadoDetalhe>('carregando');
  const [detalhe, setDetalhe] = useState<SolicitacaoAbastecimentoDetalheView | null>(null);
  const sequencia = useRef(0);

  const carregar = useCallback(async () => {
    const minha = ++sequencia.current;
    setEstado('carregando');
    try {
      const resposta = await abastecimentoApi.detalhe(solicitacaoId, itemId);
      if (minha !== sequencia.current) return;
      setDetalhe(resposta);
      setEstado('pronto');
    } catch {
      if (minha !== sequencia.current) return;
      setDetalhe(null);
      setEstado('erro');
    }
  }, [solicitacaoId, itemId]);
  useEffect(() => { if (aberto) void carregar(); }, [aberto, carregar]);

  const conteudo: ReactNode = <DetalheSolicitacaoCorpo estado={estado} detalhe={detalhe} onTentarNovamente={() => void carregar()} />;
  return (
    <FormDialog open={aberto} onOpenChange={onAbertoChange}>
      <FormDialogContent size="wide">
        <FormDialogHeader>
          <FormDialogTitle className="text-xl font-semibold">Histórico da solicitação</FormDialogTitle>
          <FormDialogDescription className="text-sm text-muted-foreground">Criação, ajustes, separações e entregas físicas, em ordem cronológica.</FormDialogDescription>
        </FormDialogHeader>
        <FormDialogBody>{conteudo}</FormDialogBody>
      </FormDialogContent>
    </FormDialog>
  );
}
